/**
 * atomberg-checkout-guard.js
 *
 * Blocks checkout (native Shopify checkout AND Razorpay Magic Checkout) until
 * atomberg-cart-edd.js has confirmed the saved pincode against every cart
 * item — both the Atomberg serviceability API (flagged products) and
 * Clickpost deliverability + EDD (every item with a SKU). This file runs no
 * API calls of its own; it only consults window.__atombergCartVerdict, the
 * single source of truth computed by atomberg-cart-edd.js.
 *
 * A saved pincode is NOT enough by itself — window.__atombergCartVerdict
 * stays null until the user has explicitly (re)checked it on THIS cart
 * page/drawer load (the Check button; see atomberg-cart-edd.js's
 * hasCheckedThisLoad gate). Proceed never silently validates a cached
 * pincode in the background: with no verdict yet, it rejects with "check
 * your pincode first"; once a verdict exists, it forces one more fresh check
 * via window.__atombergRunCartVerdictCheck() (the cart may have changed
 * since that last check) before deciding.
 *
 * Loaded globally (once) from layout/theme.liquid via
 * snippets/atomberg-checkout-guard.liquid — NOT from the cart widget. The
 * widget only renders on a non-empty cart, and its <script> tags never execute
 * when the drawer re-renders via section morphing, so loading there let the
 * drawer's Proceed button reach Razorpay with no guard attached. Loading in the
 * page shell guarantees the capture listener exists on every page load.
 *
 * Intercepts the checkout click in the CAPTURE phase so it beats the inline
 * `onclick="openRzpCheckout()"` handler and the native form submit. This gate
 * applies to every checkout attempt — not just carts containing a flagged
 * product, since Clickpost deliverability is checked for every cart item
 * regardless of requires_serviceability_check.
 *
 * The Proceed button stays clickable at all times (never `disabled`) — every
 * click gets a real answer (an inline error naming what's wrong, or a fresh
 * check) rather than a silently unclickable button the shopper can't make
 * sense of.
 *
 * Depends on:
 *  - localStorage key `atomberg_pincode` (written by atomberg-delivery-check.js)
 *  - window.__atombergCartVerdict / window.__atombergRunCartVerdictCheck (atomberg-cart-edd.js)
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);

  // Loaded once globally; guard against a stray second include.
  if (w.__atombergCheckoutGuardLoaded) return;
  w.__atombergCheckoutGuardLoaded = true;

  var PINCODE_KEY = 'atomberg_pincode';
  var TOAST_MS = 4000;

  // Every control that starts checkout from the cart / drawer.
  var CHECKOUT_SELECTORS =
    '#checkout, [name="checkout"], .atomberg-cart-sticky__cta, [onclick*="openRzpCheckout"]';

  /** @returns {string} saved pincode or '' */
  function getPincode() {
    try {
      return localStorage.getItem(PINCODE_KEY) || '';
    } catch (_) {
      return '';
    }
  }

  /**
   * The delivery widget that's actually on screen. Both the cart drawer and the
   * cart page render one, and both live in the DOM at once on /cart — pick the
   * visible root (offsetParent is null for the display:none / closed one) so the
   * inline error and scroll target land on the widget the shopper can see.
   * @returns {HTMLElement|null}
   */
  function activeRoot() {
    var roots = document.querySelectorAll('[data-cart-edd-root]');
    for (var i = 0; i < roots.length; i++) {
      if (/** @type {HTMLElement} */ (roots[i]).offsetParent !== null) {
        return /** @type {HTMLElement} */ (roots[i]);
      }
    }
    return /** @type {HTMLElement|null} */ (roots[0] || null);
  }

  /** @type {HTMLElement|null} */
  var toastEl = null;
  /** @type {number|null} */
  var toastTimer = null;

  function hideToast() {
    if (!toastEl) return;
    toastEl.classList.remove('atomberg-checkout-toast--visible');
    if (toastTimer !== null) {
      clearTimeout(toastTimer);
      toastTimer = null;
    }
  }

  /**
   * @param {string} message
   * @param {HTMLElement} trigger
   */
  function showToast(message, trigger) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'atomberg-checkout-toast';
      toastEl.setAttribute('role', 'alert');
      toastEl.setAttribute('aria-live', 'assertive');
      var icon = document.createElement('span');
      icon.className = 'atomberg-checkout-toast__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg viewBox="0 0 24 24" width="22" height="22" fill="none">' +
        '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '</svg>';
      var msg = document.createElement('span');
      msg.className = 'atomberg-checkout-toast__message';
      toastEl.appendChild(icon);
      toastEl.appendChild(msg);
      /** @type {any} */ (toastEl)._msg = msg;
    }
    // The cart drawer is a native <dialog> in the top layer — a body-level
    // toast would sit behind it. Mount inside whichever dialog the checkout
    // button lives in; fall back to body on the cart page.
    var host =
      (trigger.closest && trigger.closest('dialog')) ||
      document.querySelector('dialog[open]') ||
      document.body;
    if (toastEl.parentNode !== host) host.appendChild(toastEl);
    // Sit the toast just above the Proceed bar when that bar is pinned to the
    // viewport bottom (cart drawer + mobile cart page). On the desktop cart page
    // the bar is static inside a sidebar — anchoring to it would fling the toast
    // up the page, so fall back to the CSS default bottom there.
    var bar = /** @type {HTMLElement|null} */ (
      trigger.closest ? trigger.closest('.atomberg-cart-sticky') : null
    );
    var rect = bar ? bar.getBoundingClientRect() : null;
    if (rect && window.innerHeight - rect.bottom < 120) {
      toastEl.style.bottom = window.innerHeight - rect.top + 12 + 'px';
    } else {
      toastEl.style.bottom = '';
    }
    /** @type {any} */ (toastEl)._msg.textContent = message;
    // Force a reflow so the enter transition runs even right after re-append.
    void toastEl.offsetWidth;
    toastEl.classList.add('atomberg-checkout-toast--visible');
    if (toastTimer !== null) clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, TOAST_MS);
  }

  /**
   * Show a blocking message near the delivery widget, flash a toast, and scroll
   * the widget into view.
   * @param {string} message
   * @param {HTMLElement} trigger
   */
  function reject(message, trigger) {
    showToast(message, trigger);
    var root = activeRoot();
    var msgEl = /** @type {HTMLElement|null} */ (
      root ? root.querySelector('[data-adc-message]') : null
    );
    var rowText = /** @type {HTMLElement|null} */ (
      root ? root.querySelector('[data-adc-summary]:not([hidden]) [data-adc-v2-bad-text]') : null
    );
    // The not-serviceable row already shows this exact message — a second copy under it is noise
    var shownInRow = !!rowText && !rowText.hidden && rowText.textContent === message;
    if (msgEl && !shownInRow) {
      msgEl.textContent = message;
      // Reuse the delivery widget's own error styling (red via brand token).
      msgEl.className = 'atomberg-delivery-check__message atomberg-delivery-check__message--error';
      msgEl.hidden = false;
    }
    if (root && root.scrollIntoView) {
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Re-fire the original checkout control once the verdict has cleared it.
   * @param {HTMLElement} trigger
   */
  function proceed(trigger) {
    trigger.setAttribute('data-atomberg-guard-passed', '1');
    trigger.click();
  }

  /** @param {string} pincode @returns {string} */
  function defaultBlockMessage(pincode) {
    return "Sorry, we don't deliver to pincode " + pincode + ' for an item in your cart.';
  }

  /**
   * Show a "Checking…" state on the trigger while the fresh verdict check
   * runs, so a network round trip never looks like a dead click.
   * @param {HTMLElement} trigger
   * @returns {() => void} restores the original disabled/text state
   */
  function showChecking(trigger) {
    var btn = /** @type {HTMLButtonElement} */ (trigger);
    var wasDisabled = btn.disabled;
    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Checking delivery…';
    return function restore() {
      btn.disabled = wasDisabled;
      btn.textContent = originalText;
    };
  }

  document.addEventListener(
    'click',
    function (e) {
      var target = /** @type {HTMLElement} */ (e.target);
      var trigger = /** @type {HTMLElement|null} */ (
        target && target.closest ? target.closest(CHECKOUT_SELECTORS) : null
      );
      if (!trigger) return;

      // Second pass after a successful async check — let this one through.
      if (trigger.getAttribute('data-atomberg-guard-passed') === '1') {
        trigger.removeAttribute('data-atomberg-guard-passed');
        return;
      }

      // A check is already in flight for this trigger — ignore impatient
      // repeat clicks rather than racing two verdict checks.
      if (trigger.getAttribute('data-atomberg-guard-checking') === '1') {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      // Every checkout attempt is gated on pincode + deliverability, even
      // when the cart has zero products flagged requires_serviceability_check
      // — Clickpost is validated for every cart item in atomberg-cart-edd.js.
      e.preventDefault();
      e.stopImmediatePropagation();

      var pincode = getPincode();
      if (!/^\d{6}$/.test(pincode)) {
        reject('Please check your pincode first.', trigger);
        return;
      }

      // A saved pincode alone is not enough — atomberg-cart-edd.js only sets
      // a verdict once the user has explicitly (re)checked it on this cart
      // page/drawer load (Check button, or a prior Proceed attempt). Without
      // that, Proceed must not silently validate the cached pincode in the
      // background; it must send the user to actually check it first.
      if (w.__atombergCartVerdict == null) {
        reject('Please check your pincode first.', trigger);
        return;
      }

      var btn = trigger;

      // Never trust a cached verdict at click time — the cart may have
      // changed since atomberg-cart-edd.js last ran (a new item added via a
      // path that didn't fire cart:update, or a race between that async
      // check and this click) and the verdict is keyed only by pincode, not
      // by cart contents. Force a fresh check against the CURRENT cart on
      // every Proceed attempt and wait for it before deciding.
      var runVerdictCheck = w.__atombergRunCartVerdictCheck;
      if (typeof runVerdictCheck !== 'function') {
        // atomberg-cart-edd.js didn't load — misconfigured store, fail open.
        proceed(btn);
        return;
      }

      btn.setAttribute('data-atomberg-guard-checking', '1');
      var restore = showChecking(btn);
      runVerdictCheck()
        .then(function () {
          restore();
          btn.removeAttribute('data-atomberg-guard-checking');
          var verdict = w.__atombergCartVerdict;
          // Fail CLOSED: open checkout only on a positive, current verdict for
          // THIS pincode (every item has an EDD and every flagged product a
          // serviceable response — see atomberg-cart-edd.js). A verdict that is
          // missing, still in flight, or left over from a different pincode is
          // NOT a confirmation, so it must block rather than fall through to
          // Razorpay Magic Checkout.
          if (verdict && verdict.pincode === pincode && !verdict.blocked) {
            proceed(btn);
          } else if (verdict && verdict.pincode === pincode && verdict.blocked) {
            reject(verdict.message || defaultBlockMessage(pincode), btn);
          } else {
            reject('Please check delivery for your pincode again.', btn);
          }
        })
        .catch(function () {
          // The check itself is designed to never reject — but if it somehow
          // does, fail open rather than leaving the button stuck disabled.
          restore();
          btn.removeAttribute('data-atomberg-guard-checking');
          proceed(btn);
        });
    },
    true // capture phase — runs before inline onclick / form submit
  );
})();
